import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientReportWithProductsEntity, SprintProductStatus, useClientSprintProducts } from '@data';
import { useOrganizationId } from '@app/utils/hooks';
import { Loader } from '@ui';
import ClientReportCard from '../sprintProducts/ClientReportCard';
import { isSprintVisited } from '../visitedSprints';

/**
 * Every sprint report that has been sent to this reader, newest sprint first.
 *
 * The landing for somebody who was mailed reports without being given the products they belong to: there
 * is no product page for them to read, so the reports are the whole of what there is. One row per report
 * rather than one per sprint, each naming its product — this list crosses products, so a row that only
 * said "Sprint 4" would not say which report it was. Nothing here leads anywhere but a report: the
 * product's dashboard, team and quality stay behind the access an admin grants at the client.
 */
const SentReports = (): JSX.Element => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();

  const statuses = [SprintProductStatus.closed, SprintProductStatus.approved, SprintProductStatus.invoiced];
  const { data: sprintProducts, isLoading } = useClientSprintProducts(organizationId, { statuses });

  // The endpoint answers newest sprint first, which is the order these are read in. Each report stands
  // for its own product, which is the shape the card's actions read.
  const reports = useMemo<ClientReportWithProductsEntity[]>(
    () => (sprintProducts?.items ?? []).map(item => ({ ...item, products: [{ ...item.product, report: item }] })),
    [sprintProducts],
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl">
      <h1 className="mb-6 text-base font-bold text-foreground md:text-lg">{t('titles.reports')}</h1>
      <div className="flex flex-col">
        {reports.map((report, index) => (
          /* The list assembles newest first with a beat between rows, so the order it is in is the order it
             arrives in. Capped at the sixth: past that the waiting outlasts the reading.

             A fade rather than a slide, deliberately: arriving here is itself a page transition, which
             swipes the whole list across, and rows sliding inside a sliding page is two movements telling
             the reader the same thing. */
          <div
            key={report.id}
            className="duration-300 ease-out animate-in fade-in-0 motion-reduce:animate-none"
            style={{ animationDelay: `${Math.min(index, 5) * 0.06}s` }}
          >
            <ClientReportCard
              report={report}
              organizationId={organizationId}
              productName={report.product?.name}
              showNewBadge={!isSprintVisited(report.product?.id, report.sprint?.id)}
              // A report they have already answered is not waiting on them, the same rule the report's own
              // actions follow.
              isApprovable={report.status === SprintProductStatus.closed}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default SentReports;
